import { FormGroup } from '@angular/forms';
import { UtilService } from '../../../../../shared/services/util.service';

/**
 * As contas de DINHEIRO da seção Pagamento — cobertura dos recebimentos, rateio por centro de
 * custo e conversão para centavos —, num ponto só. Quem exibe (`vd-pagamento`, `vd-centro-custo`)
 * e quem valida (`venda-pagamento.validacao.ts`) chamam as mesmas funções, para o número da tela
 * ser o mesmo que o salvar compara.
 *
 * Espelho da folha (`VendaController`): `ToleranciaCentavo = 0,01`, cobertura por igualdade do
 * total em 2 casas, rateio com Σ% = 100 e Σ R$ = total, os dois dentro da tolerância.
 */

/** `VendaController.ToleranciaCentavo` — o mesmo número, para a tela recusar o que a folha recusa. */
export const TOLERANCIA_CENTAVO = 0.01;

/**
 * Um valor em reais como inteiro de centavos, decidido pelo ABNT em 2 casas. O `Math.round` só
 * remove o ruído binário de `× 100` (12,34 × 100 = 1233,9999…) — a regra do meio já foi aplicada.
 */
export function centavos(valor: number): number {
    return Math.round(UtilService.arredondarAbnt(Number(valor) || 0, 2) * 100);
}

/** Σ do campo `valor` dos recebimentos, em 2 casas ABNT (folha: `pagamentos.Sum(p => p.valor)`). */
export function somarRecebimentos(recebimentos: FormGroup[]): number {
    return UtilService.arredondarAbnt(
        recebimentos.reduce((soma, r) => soma + (Number(r.get('valor')?.value) || 0), 0),
        2,
    );
}

/**
 * `total − lançado` em centavos: positivo falta, negativo excede, zero fecha. A folha compara os
 * dois `decimal` por igualdade; em 2 casas, comparar centavos inteiros é a mesma pergunta.
 */
export function diferencaEmCentavos(totalDoDocumento: number, lancado: number): number {
    return centavos(totalDoDocumento) - centavos(lancado);
}

/** Σ do campo `percentual` das linhas do rateio, em 2 casas ABNT. */
export function somarPercentuaisDoRateio(linhas: FormGroup[]): number {
    return UtilService.arredondarAbnt(
        linhas.reduce((soma, l) => soma + (Number(l.get('percentual')?.value) || 0), 0),
        2,
    );
}

/** Σ do campo `valor` das linhas do rateio, em 2 casas ABNT. */
export function somarValoresDoRateio(linhas: FormGroup[]): number {
    return UtilService.arredondarAbnt(
        linhas.reduce((soma, l) => soma + (Number(l.get('valor')?.value) || 0), 0),
        2,
    );
}

/**
 * Valores do rateio a partir dos percentuais (§8.10): em centavos, cada linha com o ABNT do seu
 * `total × %`, e o resto na ÚLTIMA quando os percentuais fecham 100% — assim Σ R$ = total por
 * construção, que é o que a folha exige.
 */
export function distribuirRateioEmCentavos(totalDoDocumento: number, percentuais: number[]): number[] {
    if (!percentuais.length) return [];

    const totalC = centavos(totalDoDocumento);
    const somaPercentual = UtilService.arredondarAbnt(percentuais.reduce((a, p) => a + (Number(p) || 0), 0), 2);
    const fechaCem = Math.abs(somaPercentual - 100) <= TOLERANCIA_CENTAVO;
    let distribuido = 0;

    return percentuais.map((percentual, i) => {
        const ehUltima = i === percentuais.length - 1;
        const c = ehUltima && fechaCem
            ? totalC - distribuido
            : centavos((totalDoDocumento * (Number(percentual) || 0)) / 100);

        distribuido += c;

        return c / 100;
    });
}

/** O percentual de uma linha do rateio a partir do valor digitado, em 2 casas ABNT. */
export function percentualDoRateio(valor: number, totalDoDocumento: number): number {
    if (totalDoDocumento <= 0) return 0;

    return UtilService.arredondarAbnt(((Number(valor) || 0) / totalDoDocumento) * 100, 2);
}
